/**
 * GOAL 2026-09-05 C1.4 — the HISTORICAL PRESET REGISTER.
 *
 * A preset is a named historical SHAPE used as a stress input, never a
 * forecast: per calendar year, the published total return of a public series
 * per ASSET CLASS (the datasets are by asset class; growth in the engine is by
 * wrapper, and C2.1 applies a preset through the firm's own asset mix per
 * wrapper — no mix ships, owner decision N-C4). CPI and Bank Rate travel with
 * the year so an inflation/rate seed can be offered from the same record.
 *
 * Review gate, mirroring the tax rules (`packages/tax/src/version.ts`):
 *
 *  - every figure carries the series it came from, the URL and the retrieval
 *    date — a figure with no citation is a `null` step, never a guess;
 *  - a preset reaches the scenario editor ONLY when `review.reviewedBy` and
 *    `reviewedAt` name a human for the preset's current `version`
 *    (`reviewedHistoricalPresets()`); a candidate is data, not product;
 *  - `scripts/check-preset-review-gate.mjs` blocks a release when a preset
 *    marked `offered` lacks a current human review (owner decision N-C1: an
 *    unreviewed preset is a CI block, like an unreviewed tax rule). Candidates
 *    not yet offered are listed by the gate as HIDDEN, and the register test
 *    fails closed if one ever leaks past `reviewedHistoricalPresets()`.
 *
 * Client-facing naming (owner decision N-C2): neutral pattern label first, the
 * period in brackets — "Markets fall then rise (shaped on 2008–2012)". Never a
 * verdict, never "recover", never probability.
 */
/** Wording the register may never carry — the readout describes lines, not verdicts. */
export const PRESET_BANNED_WORDS = /\b(?:surviv(?:e|es)|safe|recover(?:s|ed|y|ing)?|resilient|worst case|prudent|likely|probab\w*|chance of|crash|crisis|lowest point|back to the plan|recommend\w*|best|optimal|should|guarantee\w*|will happen|based on)\b/i;
/**
 * Same placeholder rule as the tax review gate, tightened after review: a
 * machine attestation, a role word ("Owner", "Reviewer"), a generic token
 * ("Name", "test", "admin") or a blank is not a named human. Separators are
 * normalised so `dev_user` and `dev-user` read as "dev user".
 */
export function isPlaceholderPresetReviewer(reviewedBy) {
    if (reviewedBy === null)
        return true;
    const name = reviewedBy.replace(/[_\-.]+/g, ' ').trim();
    if (name.length < 3)
        return true;
    return /\b(temp|dev|placeholder|tbc|todo|pending|claude|automated|bot|your name|owner|reviewer|name|test|admin|unknown|n\/a|none)\b/i.test(name);
}
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** Reviewed = a named human signed THIS version on a real date. */
export function isPresetReviewed(preset) {
    const r = preset.review;
    return (r.reviewedBy !== null &&
        r.reviewedAt !== null &&
        ISO_DATE.test(r.reviewedAt) &&
        !Number.isNaN(Date.parse(r.reviewedAt)) &&
        r.reviewedVersion === preset.version &&
        !isPlaceholderPresetReviewer(r.reviewedBy));
}
/** Every year that still has an unsourced figure — the reviewer's list. */
export function unsourcedFigures(preset) {
    const out = [];
    for (const y of preset.years) {
        for (const [cls, v] of Object.entries(y.totalReturnBpsByAssetClass)) {
            if (v === null)
                out.push(`${y.year} ${cls}`);
        }
        if (y.cpiBps === null)
            out.push(`${y.year} cpi`);
        if (y.bankRateEndBps === null)
            out.push(`${y.year} bankRate`);
    }
    return out;
}
const RETRIEVED = '2026-09-06';
const FTSE_ALL_SHARE_2016_FACTSHEET = {
    series: 'FTSE All-Share Index — Year-on-Year Performance, Total Return (GBP)',
    publisher: 'FTSE Russell (factsheet "FTSE All-Share Indices", data as at 30 September 2016)',
    url: 'https://www.investmentofficer.nl/sites/default/files/FTSE_All_Share_Index.pdf',
    retrievedAt: RETRIEVED,
    note: 'Row "FTSE All-Share", columns 2008–2012: −29.9, 30.1, 14.5, −3.5, 12.3. A mirrored copy of the FTSE Russell factsheet; the reviewer should confirm against research.ftserussell.com (issue ASX).',
};
const FTSE_ALL_SHARE_2026_FACTSHEET = {
    series: 'FTSE All-Share Index — Year-on-Year Performance, Total Return (GBP)',
    publisher: 'FTSE Russell (factsheet "FTSE All-Share Indices", data as at 31 August 2026)',
    url: 'https://research.ftserussell.com/Analytics/FactSheets/Home/DownloadSingleIssue?issueName=ASX&isManual=True',
    retrievedAt: RETRIEVED,
    note: 'Row "FTSE All-Share", columns 2020, 2021: −9.8, 18.3.',
};
const GILTS_FACTSHEET = {
    series: 'FTSE Actuaries UK Conventional Gilts All Stocks Index — Total Return (GBP)',
    publisher: 'FTSE Russell (factsheet, data as at 31 August 2026)',
    url: 'https://research.ftserussell.com/Analytics/FactSheets/Home/DownloadSingleIssue?issueName=BG05&isManual=True',
    retrievedAt: RETRIEVED,
    note: 'The current factsheet carries only a five-year chart and no year-on-year table; NO calendar-year figure for 2008–2012 or 2020–2021 could be read from a fetchable public page on the retrieval date. Every gilts step is therefore null (unsourced) until the reviewer supplies the series (FTSE Russell historical factsheets or the index provider).',
};
const GILTS_ISHARES_2026_FACTSHEET = {
    series: 'FTSE Actuaries UK Conventional Gilts All Stocks (Midday) Index (GBP) — Calendar Year Performance',
    publisher: 'BlackRock (factsheet "iShares UK Gilts All Stocks Index Fund (UK) Class H", performance as at 31 August 2026)',
    url: 'https://www.blackrock.com/uk/literature/fact-sheet/ishares-uk-gilts-all-stocks-index-fund-uk-class-h-accu-gbp-factsheet-gb00bpfjdd09-gb-en-individual.pdf',
    retrievedAt: '2026-09-16',
    note: 'Table "Calendar Year Performance", row "Target 1" (the fund\'s benchmark index, not the fund), columns 2020, 2021: 8.50, −5.09. The factsheet states the benchmark moved from a close-of-business to a midday valuation on 29 April 2016, with earlier history simulated by the index provider; 2020 and 2021 are after the change. A fund provider\'s factsheet reporting the index, not FTSE Russell\'s own — the reviewer may confirm against the index provider.',
};
const UK_HPI = {
    series: 'UK House Price Index — United Kingdom, all property types, percentage annual change, December',
    publisher: 'HM Land Registry / ONS (UK HPI open data)',
    url: 'https://landregistry.data.gov.uk/data/ukhpi/region/united-kingdom/month/2008-12.json',
    retrievedAt: RETRIEVED,
    note: '`percentageAnnualChange` for each December: 2008 −14.9, 2009 4.4, 2010 0.4, 2011 −1.0, 2012 1.1, 2020 7.0, 2021 7.3 (one JSON document per month; substitute 2008-12 in the URL). The brief names the Nationwide HPI; Nationwide’s data pages are script-rendered and not fetchable, so the official UK HPI is recorded instead — the reviewer decides whether to keep it.',
};
const ONS_CPI = {
    series: 'CPI ANNUAL RATE 00: ALL ITEMS 2015=100 (series D7G7), annual averages',
    publisher: 'Office for National Statistics',
    url: 'https://www.ons.gov.uk/generator?format=csv&uri=/economy/inflationandpriceindices/timeseries/d7g7/mm23',
    retrievedAt: RETRIEVED,
    note: 'Annual rows: 2008 3.6, 2009 2.2, 2010 3.3, 2011 4.5, 2012 2.8, 2020 0.9, 2021 2.6.',
};
const BOE_BANK_RATE = {
    series: 'Official Bank Rate history (changes)',
    publisher: 'Bank of England',
    url: 'https://www.bankofengland.co.uk/boeapps/database/Bank-Rate.asp',
    retrievedAt: RETRIEVED,
    note: 'Year-end rate from the change table: 2007 5.50 (06 Dec 07), 2008 2.00 (04 Dec 08), 2009–2012 0.50 (05 Mar 09, unchanged to Aug 2016), 2019 0.75 (02 Aug 18, unchanged to 11 Mar 20), 2020 0.10 (19 Mar 20), 2021 0.25 (16 Dec 21).',
};
const year = (y, equity, bonds, property, cpiBps, bankRateEndBps) => ({
    year: y,
    totalReturnBpsByAssetClass: { equity, bonds, property },
    cpiBps,
    bankRateEndBps,
});
/**
 * The register. Two candidates at launch (owner decision N-C1: 2008 and
 * 2020), both UNREVIEWED and NOT offered until a named human signs the
 * figures — the gilts column and the property series are the reviewer's open
 * items (see each source note).
 */
/** Freeze every level so no consumer can flip `offered` or `review` at runtime. */
function deepFreeze(value) {
    if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
        Object.freeze(value);
        for (const v of Object.values(value))
            deepFreeze(v);
    }
    return value;
}
export const HISTORICAL_PRESETS = deepFreeze([
    {
        id: 'gfc-2008',
        version: '2026-09-06.2',
        name: 'Markets fall about 30%, then rise (shaped on 2008–2012)',
        period: { from: 2008, to: 2012 },
        summary: 'UK equity total return −29.9% in 2008, +30.1% in 2009, then +14.5%, −3.5%, +12.3%; UK house prices (December on December) −14.9% in 2008, +4.4% in 2009, then within ±1.1% a year; CPI 3.6% → 2.2% → 3.3% → 4.5% → 2.8%; Bank Rate 5.50% at end-2007, 2.00% at end-2008, 0.50% from March 2009.',
        bankRateStartBps: 550,
        years: [
            year(2008, -2990, null, -1490, 360, 200),
            year(2009, 3010, null, 440, 220, 50),
            year(2010, 1450, null, 40, 330, 50),
            year(2011, -350, null, -100, 450, 50),
            year(2012, 1230, null, 110, 280, 50),
        ],
        sources: [FTSE_ALL_SHARE_2016_FACTSHEET, GILTS_FACTSHEET, UK_HPI, ONS_CPI, BOE_BANK_RATE],
        review: {
            reviewedBy: 'Caleb Saunderson',
            reviewedAt: '2026-09-11',
            reviewedVersion: '2026-09-06.2',
        },
        offered: false,
    },
    {
        id: 'pandemic-2020',
        version: '2026-09-16.1',
        name: 'Markets fall one year, rise the next (shaped on 2020–2021)',
        period: { from: 2020, to: 2021 },
        summary: 'UK equity total return −9.8% in calendar 2020 (during the year prices fell much further before ending 2020 down 9.8%) and +18.3% in 2021 — a short fall in calendar-year form, milder than the intra-year move; UK conventional gilts total return +8.5% in 2020 and −5.1% in 2021; UK house prices (December on December) +7.0% then +7.3%; CPI 0.9% then 2.6%; Bank Rate 0.75% at end-2019, 0.10% at end-2020, 0.25% at end-2021.',
        bankRateStartBps: 75,
        years: [year(2020, -980, 850, 700, 90, 10), year(2021, 1830, -509, 730, 260, 25)],
        sources: [
            FTSE_ALL_SHARE_2026_FACTSHEET,
            GILTS_ISHARES_2026_FACTSHEET,
            UK_HPI,
            ONS_CPI,
            BOE_BANK_RATE,
        ],
        review: {
            reviewedBy: 'Caleb Saunderson',
            reviewedAt: '2026-09-11',
            reviewedVersion: '2026-09-06.2',
        },
        offered: false,
    },
]);
/** The presets an adviser may be offered: reviewed by a named human for this version, and offered. */
export function reviewedHistoricalPresets(presets = HISTORICAL_PRESETS) {
    return presets.filter((p) => p.offered && isPresetReviewed(p));
}
//# sourceMappingURL=historical-presets.js.map